import type { ReactNode } from "react";
import { Icon } from "../icons/Icon";
import { Pill } from "../primitives/Pill";
import { Tabs } from "../primitives/Misc";
import { Button } from "../primitives/Button";
import { cx, formatDate } from "../utils/format";
import type { Navigate } from "./AppShell";

export interface InfoRow { label: string; value: ReactNode; }

export interface InfoTicket { id: string; href: string; number: number; subject: string; status: string; updatedAt: string; /** the ticket that is open right now */ current?: boolean; }

/** The customer's other tickets, in the Tickets tab: subject first, number and date beneath, the status as a chip. Each row opens its ticket. */
export function InfoTicketList({ tickets, onNavigate }: { tickets: InfoTicket[]; onNavigate: Navigate }) {
  return (
    <ul className="ob-info-tickets">
      {tickets.map((t) => (
        <li key={t.id}>
          <a className={cx("ob-info-ticket", t.current && "ob-info-ticket--current")} href={t.href} aria-current={t.current ? "page" : undefined} onClick={(e) => onNavigate(t.href, e)}>
            <span className="ob-info-ticket__subject">{t.subject}</span>
            <span className="ob-info-ticket__meta"><span>#{t.number}</span><span>·</span><span>{formatDate(t.updatedAt)}</span>{t.current && <><span>·</span><span>this ticket</span></>}</span>
            <span className="ob-ticket-row__chip ob-info-ticket__status">{t.status}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

/** Beside the ticket on very wide screens; below that it is a drawer that slides in from the right when `open`.
 *  Its head is a band like the ones over the list and the ticket, holding the tabs: Profile (was "Profile details" until 2026-11-02) and Tickets, and (the ticket discussion
 *  round, 2026-11-02) Discussion when the app passes one, with its unread count; its content fills the body and scrolls itself. */
export function InfoSidebar({ tab, onTab, rows, extra, payment, insight, children, open, onClose, discussion }: {
  tab: string; onTab: (id: string) => void; rows: InfoRow[]; /** drawer mode: shown */ open?: boolean; onClose?: () => void;
  /** the ticket discussion round: the third tab's content (the narrow chat) and its number (the reader's unread lines) */
  discussion?: { content: ReactNode; count?: number };
  /** M12: right after the information, e.g. the customer's addresses (`CustomerAddresses`) and, since the customer sources panel round
   *  (2026-11-04), one `CustomerSource` section per source of what is known about the customer (the "From <host>" blocks are gone) */
  extra?: ReactNode;
  payment?: { title: string; sub: string; amount: string; initial: string };
  insight?: { summary: string; status: string };
  children?: ReactNode;
}) {
  return (
    <>
    {open && <div className="ob-info__backdrop" onClick={onClose} aria-hidden="true" />}
    <aside className={cx("ob-info", open && "ob-info--open")} aria-label="Customer">
      <div className="ob-info__band">
        <Tabs variant="band" items={[{ id: "profile", label: "Profile" }, { id: "tickets", label: "Tickets" }, ...(discussion ? [{ id: "discussion", label: "Discussion", count: discussion.count }] : [])]} active={tab} onChange={onTab} />
        {onClose && <Button variant="icon" size="sm" icon="x" aria-label="Close customer details" className="ob-info__close" onClick={onClose} />}
      </div>
      <div className={cx("ob-info__body", discussion && tab === "discussion" && "ob-info__body--chat")}>
      {discussion && tab === "discussion" ? discussion.content : tab === "profile" ? (
        <>
          <h3 className="ob-info__title">Information</h3>
          <dl className="ob-info__list">
            {rows.map((r) => <div className="ob-info__row" key={r.label}><dt>{r.label}</dt><dd>{r.value}</dd></div>)}
          </dl>
          {extra}
          {payment && (
            <>
              <h3 className="ob-info__title">Orders / billing</h3>
              <div className="ob-info__card">
                <span className="ob-info__card-icon">{payment.initial}</span>
                <div>
                  <div className="ob-info__card-title">{payment.title} <Icon name="openInNew" size="xs" /></div>
                  <div className="ob-info__card-sub">{payment.sub}</div>
                </div>
                <span className="ob-info__card-amount">{payment.amount}</span>
              </div>
            </>
          )}
          {insight && (
            <>
              <h3 className="ob-info__title">AI insights</h3>
              <div className="ob-info__note">
                Summary: {insight.summary}
                <div className="ob-info__note-status">Status: <Pill>{insight.status}</Pill></div>
              </div>
            </>
          )}
        </>
      ) : children}
      </div>
    </aside>
    </>
  );
}
